import React, { useState, useEffect, useRef } from "react";

/**
 * EditClientModal — the person's identity fields, edited from the client list.
 *
 * The UID is the only fixed thing. Display name, full name, pronouns and
 * location are all editable, because people change names, correct a name
 * entered wrong from an ISP, move, and change pronouns. Making any of that
 * immutable is a database convenience masquerading as a policy.
 *
 * Opens from the pencil on a client row — the same pencil that used to edit
 * the display name alone, inline. A modal rather than inline because four
 * fields don't fit in a list row, and because pronouns have a consequence
 * worth a moment's attention (below).
 *
 * PRONOUNS ARE THE ONE WITH A CONSEQUENCE. TTS bakes them into authored
 * narration. Saving does not regenerate anything — that is the coordinator's
 * call and a PE round trip — but the server says when narration exists and is
 * now stale, and this reports it back through onSaved so the list can say so.
 */

const PRONOUN_PRESETS = ["she/her", "he/him", "they/them"];

export default function EditClientModal({ client, onClose, onSaved }) {
  // The list rows this opens from carry a display name and not much else.
  // Seeding the form from a row that has no pronouns would save "" and clear
  // them — so the form is seeded from the record itself, fetched on open.
  const [form, setForm]     = useState(null);
  const [custom, setCustom] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const firstRef = useRef();

  useEffect(() => {
    let live = true;
    fetch(`/api/clients/${client.id}`)
      .then(r => { if (!r.ok) throw new Error("Couldn't load this client."); return r.json(); })
      .then(c => {
        if (!live) return;
        const pronouns = c.pronouns || "";
        setForm({
          display_name:     c.display_name || "",
          full_name:        c.full_name || "",
          pronouns,
          location_display: c.location_display || "",
          text_cards:       !!c.text_cards,
        });
        setCustom(!!pronouns && !PRONOUN_PRESETS.includes(pronouns));
        setTimeout(() => firstRef.current?.focus(), 0);
      })
      .catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [client.id]);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function save() {
    if (!form.display_name.trim()) { setError("Display name is required."); return; }
    if (!form.full_name.trim())    { setError("Full name is required."); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${client.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name:     form.display_name.trim(),
          full_name:        form.full_name.trim(),
          pronouns:         form.pronouns.trim(),
          location_display: form.location_display.trim(),
          text_cards:       form.text_cards,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't save.");
      onSaved?.(d);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.box} onKeyDown={e => e.key === "Escape" && onClose()}>
        <div style={m.header}>
          <h2 style={m.title}>Edit {client.display_name}</h2>
          <button style={m.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <p style={m.error}>{error}</p>}
        {!form && !error && <p style={m.loading}>Loading…</p>}

        {form && <>
        <label style={m.label}>
          Display name
          <input ref={firstRef} style={m.input} value={form.display_name} disabled={busy}
                 onChange={e => set("display_name", e.target.value)}
                 onKeyDown={e => e.key === "Enter" && save()} />
        </label>

        <label style={m.label}>
          Full name
          <input style={m.input} value={form.full_name} disabled={busy}
                 onChange={e => set("full_name", e.target.value)} />
        </label>

        <div style={m.label}>
          Pronouns
          <div style={m.chips}>
            {PRONOUN_PRESETS.map(p => (
              <button key={p} type="button" disabled={busy}
                      style={{ ...m.chip, ...(form.pronouns === p && !custom ? m.chipOn : {}) }}
                      onClick={() => { setCustom(false); set("pronouns", p); }}>
                {p}
              </button>
            ))}
            <button type="button" disabled={busy}
                    style={{ ...m.chip, ...(custom ? m.chipOn : {}) }}
                    onClick={() => { setCustom(true); if (PRONOUN_PRESETS.includes(form.pronouns)) set("pronouns", ""); }}>
              other
            </button>
          </div>
          {custom && (
            <input style={{ ...m.input, marginTop: 6 }} value={form.pronouns} placeholder="e.g. she/they"
                   disabled={busy} onChange={e => set("pronouns", e.target.value)} />
          )}
        </div>

        <label style={m.label}>
          Location
          <input style={m.input} value={form.location_display} disabled={busy}
                 placeholder="City or area — not an address"
                 onChange={e => set("location_display", e.target.value)} />
        </label>

        {/* PE v3.7 (Oct 27). Wording is a first draft for David. */}
        <label style={{ ...m.label, flexDirection: "row", alignItems: "flex-start", gap: "var(--space-2)" }}>
          <input type="checkbox" checked={form.text_cards} disabled={busy}
                 onChange={e => set("text_cards", e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            Text cards in streams
            <span style={{ display: "block", fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" }}>
              Where no photo fits what's being said, show the narration's own words instead of an unrelated photo.
              Applies the next time a stream is authored.
            </span>
          </span>
        </label>

        <div style={m.footer}>
          <button style={m.cancelBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...m.saveBtn, ...(busy ? m.disabled : {}) }} onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
        </>}
      </div>
    </div>
  );
}

const m = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  box:       { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-8)", width: 480, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  close:     { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  label:     { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  input:     { font: "inherit", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  chips:     { display: "flex", gap: 6, flexWrap: "wrap" },
  chip:      { font: "inherit", fontSize: "var(--text-xs)", padding: "5px 10px", borderRadius: 999, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer" },
  chipOn:    { border: "1px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  footer:    { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  loading:   { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", margin: 0 },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", margin: 0 },
  cancelBtn: { font: "inherit", fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer" },
  saveBtn:   { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  disabled:  { opacity: 0.5, cursor: "not-allowed" },
};
